import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  EnhancedLoginForm,
  EnhancedRegisterForm,
  ForgotPasswordForm,
  PasswordResetForm,
  AuthSuccessMessage
} from './AuthComponents';
import LanguageSelector from './LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthHandler = ({ 
  onAuthSuccess, 
  onBackToLanding, 
  onShowPrivacyPolicy, 
  onShowTermsOfService 
}) => {
  const { t } = useTranslation();
  const [authView, setAuthView] = useState('login'); // login, register, forgot, reset, success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resetToken, setResetToken] = useState('');

  // Check for reset token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const action = urlParams.get('action');
    
    if (token && action === 'reset') {
      setResetToken(token);
      setAuthView('reset');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleLogin = async (data) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: data.username,
        password: data.password
      });
      
      const { access_token, user } = response.data;
      
      // Store authentication data
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user_data', JSON.stringify(user));
      
      // Store remember me preference
      if (data.rememberMe) {
        localStorage.setItem('remember_me', 'true');
      }
      
      onAuthSuccess(user);
    } catch (error) {
      setError(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (data) => {
    setLoading(true);
    setError('');
    
    try {
      // For now, only admins can create users, so we'll show a message
      // In a real-world scenario, you might have self-registration enabled
      setError(t('auth.registrationDisabled'));
      
      // If self-registration was enabled, the code would be:
      // const response = await axios.post(`${API}/auth/register`, {
      //   username: data.username,
      //   email: data.email,
      //   password: data.password,
      //   full_name: data.fullName
      // });
      // setSuccessMessage(t('auth.registrationSuccess'));
      // setAuthView('success');
    } catch (error) {
      setError(error.response?.data?.detail || t('auth.registrationError'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (data) => {
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: data.email
      });
      
      setAuthView('forgot');
      setSuccessMessage(t('auth.resetEmailSent'));
    } catch (error) {
      setError(error.response?.data?.detail || t('auth.forgotPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (data) => {
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: data.token,
        new_password: data.password
      });
      
      setSuccessMessage(t('auth.passwordResetSuccess'));
      setAuthView('success');
    } catch (error) {
      setError(error.response?.data?.detail || t('auth.passwordResetError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaultAdmin = async () => {
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/create-default-admin`);
      setSuccessMessage(t('auth.defaultAdminCreated'));
      setAuthView('success');
    } catch (error) {
      setError(error.response?.data?.detail || t('auth.createAdminError'));
    } finally {
      setLoading(false);
    }
  };

  const renderAuthView = () => {
    switch (authView) {
      case 'register':
        return (
          <EnhancedRegisterForm
            onSubmit={handleRegister}
            onBackToLogin={() => setAuthView('login')}
            loading={loading}
            error={error}
          />
        );
      
      case 'forgot':
        return (
          <ForgotPasswordForm
            onSubmit={handleForgotPassword}
            onBackToLogin={() => setAuthView('login')}
            loading={loading}
            error={error}
            success={!!successMessage}
          />
        );
      
      case 'reset':
        return (
          <PasswordResetForm
            onSubmit={handlePasswordReset}
            loading={loading}
            error={error}
            token={resetToken}
          />
        );
      
      case 'success':
        return (
          <AuthSuccessMessage
            message={successMessage}
            onContinue={() => {
              setAuthView('login');
              setSuccessMessage('');
              setError('');
            }}
          />
        );
      
      default:
        return (
          <EnhancedLoginForm
            onSubmit={handleLogin}
            onForgotPassword={() => setAuthView('forgot')}
            loading={loading}
            error={error}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Language Selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      {/* Back to Landing Button */}
      {onBackToLanding && (
        <div className="absolute top-4 left-4">
          <button
            onClick={onBackToLanding}
            className="flex items-center text-mg-green hover:text-mg-green/80 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </button>
        </div>
      )}
      
      {/* Main Auth Content */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        {renderAuthView()}
        
        {/* Additional Actions */}
        {authView === 'login' && (
          <div className="mt-6 space-y-2 text-center">
            <div>
              <button
                type="button"
                className="text-sm text-mg-green hover:text-mg-green/80 underline"
                onClick={() => setAuthView('register')}
              >
                {t('auth.createNewAccount')}
              </button>
            </div>
            <div>
              <button
                type="button"
                className="text-sm text-gray-600 hover:text-gray-800 underline"
                onClick={handleCreateDefaultAdmin}
                disabled={loading}
              >
                {t('auth.createDefaultAdmin')}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer with Legal Links */}
      <div className="mt-8 text-center space-y-2">
        <div className="flex justify-center space-x-4 text-xs">
          {onShowPrivacyPolicy && (
            <button
              onClick={onShowPrivacyPolicy}
              className="text-gray-500 hover:text-mg-green underline"
            >
              Privacy Policy
            </button>
          )}
          {onShowTermsOfService && (
            <button
              onClick={onShowTermsOfService}
              className="text-gray-500 hover:text-mg-green underline"
            >
              Terms of Service
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          © 2025 BUDGET DIGITAL SOLUTIONS. {t('auth.allRightsReserved')}
        </p>
      </div>
    </div>
  );
};

export default AuthHandler;